import { isCardOwned, getCard, getCardType } from "../util/cardUtil";
import { dynamicSort, getRarity, soldOutSort } from "../helpers";
import { CURR_CHAIN } from "../util/constants/networks";

const typeIdsOnChain = [];

if (CURR_CHAIN !== 7001) {
  typeIdsOnChain.push(3,6,7,11,14,18,19,28,29,34,51,52,58,60,62,63,65,72,73,74,75,81,85,86,92,94,95,109,110,111,112,121,122,128,135,138,143,144);
  typeIdsOnChain.push(148,153,161,162,165,167,169,170,172,174,180,181,182,184,188,196,199,201,203,205,206);
}else{
  typeIdsOnChain.push(3,6,7,11,14,18,19,28,29,34);
}

function delay(ms) {
  console.log("Delaying for: ", ms);
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const DEFAULT_CARD_STATE = {
  allShopCards: [],
  sortedCards: [],
  isLoadingShop: false,
  failedToLoadShop: false,
  shopLoaded: false,
};

export const CARD_MUTATIONS = {
  SET_SHOP_CARDS: "SET_SHOP_CARDS",
  LOADING_SHOP_CARDS: "LOADING_SHOP_CARDS",
  LOADING_SHOP_CARDS_FAILED: "LOADING_SHOP_CARDS_FAILED",
  SET_SORTED_CARDS: "SET_SORTED_CARDS",
  SET_CARD_BOUGHT: "SET_CARD_BOUGHT",
  SET_CARD_NOT_BOUGHT: "SET_CARD_NOT_BOUGHT",
  SET_CARD_EDITION: "SET_CARD_EDITION",
  CLEAR_STORE_CARDS: "CLEAR_STORE_CARDS",
};

const cardStore = {
  state: DEFAULT_CARD_STATE,
  mutations: {
    [CARD_MUTATIONS.CLEAR_STORE_CARDS](state) {
      state = DEFAULT_CARD_STATE;
    },
    [CARD_MUTATIONS.SET_CARD_EDITION](state, payload) {
      const { cardId, edition, isSorted } = payload;

      if (isSorted) {
        const sortedIndex = state.sortedCards.findIndex(
          (card) => card.id === cardId
        );
        state.sortedCards[sortedIndex].edition_current = edition;
      }

      const index = state.allShopCards.findIndex((card) => card.id === cardId);
      state.allShopCards[index].edition_current = edition;
    },
    [CARD_MUTATIONS.SET_CARD_BOUGHT](state, payload) {
      const { cardId, isSorted } = payload;
      const parsedCardId = parseInt(cardId);

      if (isSorted) {
        const cardSortedIndex = state.sortedCards.findIndex(
          (card) => card.id === parsedCardId
        );
        state.sortedCards[cardSortedIndex].isOwned = true;
      } else {
        const cardBoughtIndex = state.allShopCards.findIndex(
          (card) => card.id === parsedCardId
        );
        state.allShopCards[cardBoughtIndex].isOwned = true;
      }
    },
    [CARD_MUTATIONS.SET_CARD_NOT_BOUGHT](state, payload) {
      const { cardId, isSorted } = payload;
      const parsedCardId = parseInt(cardId);

      if (isSorted) {
        const cardSortedIndex = state.sortedCards.findIndex(
          (card) => card.id === parsedCardId
        );
        state.sortedCards[cardSortedIndex].isOwned = false;
      } else {
        const cardBoughtIndex = state.allShopCards.findIndex(
          (card) => card.id === parsedCardId
        );
        state.allShopCards[cardBoughtIndex].isOwned = false;
      }
    },
    /**
     * @param {DEFAULT_CARD_STATE} state
     * @param {Array<card>} payload
     *
     */
    [CARD_MUTATIONS.SET_SHOP_CARDS](state, payload) {
      state.allShopCards = [...payload].sort(soldOutSort);
      state.shopLoaded = true;
      state.isLoadingShop = false;
      state.failedToLoadShop = false;
    },
    [CARD_MUTATIONS.LOADING_SHOP_CARDS](state) {
      state.isLoadingShop = true;
      state.shopLoaded = false;
    },
    [CARD_MUTATIONS.LOADING_SHOP_CARDS_FAILED](state) {
      state.failedToLoadShop = true;
      state.shopLoaded = false;
    },
    [CARD_MUTATIONS.SET_SORTED_CARDS](state, payload) {
      const { allShopCards, sortParam } = payload;
      const shopCards = [...allShopCards];

      switch (sortParam.param) {
        case "edition_number":
          shopCards.sort(
            dynamicSort("edition_current", sortParam.isDescending, false)
          );
          break;
        case "rarity":
          shopCards.sort(
            dynamicSort(
              sortParam.param,
              sortParam.isDescending,
              true,
              getRarity
            )
          );
          break;
        default:
          shopCards.sort(dynamicSort(sortParam.param, sortParam.isDescending));
          break;
      }

      state.sortedCards = shopCards;
    },
    updateMintedCountForCard(state, payload) {
      const { cardTypeId, editionNumber } = payload;

      const cardIndex = state.allShopCards.findIndex(
        (card) => card.type_id === cardTypeId
      );
      if (cardIndex > -1) {
        state.allShopCards[cardIndex].edition_current = editionNumber;
      }

      if (state.sortedCards.length > 0) {
        const sortedIndex = state.sortedCards.findIndex(
          (card) => card.type_id === cardTypeId
        );
        state.sortedCards[sortedIndex].edition_current = editionNumber;
      }
    },
  },
  actions: {
    async setCurrentEdition({ commit, rootState }, payload) {
      try {
        const { cardId, edition, isSorted } = payload;
        const parsedId = parseInt(cardId);

        commit(CARD_MUTATIONS.SET_CARD_EDITION, {
          cardId: parsedId,
          edition,
          isSorted,
        });
      } catch (err) {
        console.error(err);
      }
    },
    async fetchStoreCards({ commit, rootState }) {
      try {
        commit(CARD_MUTATIONS.LOADING_SHOP_CARDS);
        const results = await Promise.all(
          typeIdsOnChain.map(async (id) => {
            const cardData = await getCard(
              id,
              rootState.blockChain.contracts.readOnlyZoombiesContract
            );
            if (!cardData) {
              return;
            }
            await delay(2000);
            if (rootState.blockChain.walletAddress) {
              const isOwned = await isCardOwned(
                cardData,
                rootState.blockChain.contracts.readOnlyZoombiesContract,
                rootState.blockChain.walletAddress
              );

              return {
                ...cardData,
                isOwned,
              };
            }
            return cardData;
          })
        );
        const storeCards = results.filter((result) => result !== undefined);
        commit(CARD_MUTATIONS.SET_SHOP_CARDS, storeCards);
      } catch (err) {
        console.error("Failed to load shop cards", { err });
        commit(CARD_MUTATIONS.LOADING_SHOP_CARDS_FAILED);
      }
    },
    sortCards({ commit, state }, payload) {
      commit(CARD_MUTATIONS.SET_SORTED_CARDS, {
        allShopCards: state.allShopCards,
        sortParam: payload,
      });
    },
    setCardAsBought({ commit }, payload) {
      commit(CARD_MUTATIONS.SET_CARD_BOUGHT, payload);
    },
    setCardAsNotBought({ commit }, payload) {
      commit(CARD_MUTATIONS.SET_CARD_NOT_BOUGHT, payload);
    },
    updateMintedCountForCard({ commit }, payload) {
      commit("updateMintedCountForCard", payload);
    },
    clearShopCards({ commit }) {
      commit(CARD_MUTATIONS.CLEAR_STORE_CARDS);
    },
  },
  getters: {
    getPaginatedShopCards: (state) => (pageSize, start, isSorted) => {
      const cardsToReturn = isSorted ? state.sortedCards : state.allShopCards;
      if (cardsToReturn.length === 0) {
        return [];
      }

      let pageNext, endIndex;
      if (!!start) {
        if (start + pageSize > cardsToReturn.length) {
          pageNext = null;
          endIndex = cardsToReturn.length;
        } else {
          pageNext = start + pageSize;
          endIndex = start + pageSize;
        }
      } else {
        if (pageSize > cardsToReturn.length) {
          pageNext = null;
          endIndex = cardsToReturn.length;
        } else {
          pageNext = pageSize;
          endIndex = pageSize;
        }
      }

      if (!!start) {
        // Return pagesize from this start
        return {
          cards: cardsToReturn.slice(start, endIndex),
          next: pageNext,
        };
      } else {
        return {
          cards: cardsToReturn.slice(0, endIndex),
          next: pageNext,
        };
      }
    },
    isLoadingShopCards: (state) => {
      return state.isLoadingShop;
    },
    isLoadingShopFailed: (state) => {
      return state.failedToLoadShop;
    },
    isShopLoadingFinished: (state) => {
      return state.shopLoaded;
    },
    getCardByTypeId: (state) => (typeId) => {
      const card = state.allShopCards.filter(
        (card) => card.type_id === typeId.toString()
      );
    },
    getAllShopCards: (state) => {
      return state.allShopCards;
    },
  },
};

export default cardStore;
